import { ColorSelected } from '@/components/blocks/ProductDetails/parts/buyByColor/ColorSelected';
import { ColorsPaletteDefault } from '@/components/blocks/ProductDetails/parts/buyByColor/ColorsPaletteDefault';
import { DrawerTitle } from '@/components/blocks/ProductDetails/parts/buyByColor/DrawerTitle';
import {
	buyByColorDrawerColorPaletteTitleSx,
	buyByColorDrawerContainerSx,
	checkCircleIconSx,
	colorCodeBorderSx,
	colorCodeSelectedDrawerSx,
	colorSwatchBoxSx,
	colorSwatchLowerSectionSx,
	colorSwatchTypography,
	colorSwatchUpperSectionSx,
	containerSx,
	drawerSubmitButtonSx,
	drawerSx,
	overflowContentSx,
	rangeColorsGridItemSx,
	rangeColorsGridSx,
	rangeColorsTypographySx,
} from '@/components/blocks/ProductDetails/styles/buyByColor';
import {
	capitalizeFirstLetter,
	saveColorCodeURL,
	setColorCode,
} from '@/components/blocks/ProductDetails/utils/utils';
import {
	BASE_COLOR_IDENTIFIER_SUFFIX,
	NOT_APPLICABLE,
	WHITE_BASE_COLOR_NAME,
} from '@/components/content/ProductDetails/constants';
import { USAGE_DEFINING } from '@/data/constants/catalog';
import { useLocalization } from '@/data/Localization';
import { useColorDrawerState } from '@/data/state/useColorDrawerState';
import { ChangeBaseColorPayload, LineRangeColor } from '@/data/types/ColorType';
import { ProductAttribute } from '@/data/types/Product';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
	Box,
	Button,
	Collapse,
	Grid,
	SwipeableDrawer,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { debounce } from 'lodash';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	OpenSansFontWeights,
	PASTELES_FAMILY_COLOR,
} from 'shared/custom/constants/commonConstants';

interface BuyByColorDrawerProps {
	allLineColors: LineRangeColor[] | undefined;
	superSkuAttributes?: ProductAttribute[];
	onAttributeChange: (identifier: string, value: string) => void;
}
export const BuyByColorDrawer: FC<BuyByColorDrawerProps> = ({
	allLineColors,
	superSkuAttributes,
	onAttributeChange,
}) => {
	const { BuyByColor } = useLocalization('HDMProductDetail');
	const { colorDrawerState, actions: colorDrawerActions } = useColorDrawerState();
	const {
		isOpen,
		rangeColors,
		base,
		colorCodeSelected,
		colorSelectedDrawer,
		pendingRange,
		hasConfirmedColor,
	} = colorDrawerState;
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const [collapsedColorPalette, setCollapsedColorPalette] = useState(false);
	const scrollContainerRef = useRef<HTMLDivElement>(null);

	const handleGoPalettes = useCallback(() => {
		const container = scrollContainerRef.current;
		if (container) {
			container.scrollTo({ top: 0, behavior: 'smooth' });
			setCollapsedColorPalette(false);
		}
	}, [setCollapsedColorPalette]);

	const handleScroll = useMemo(() => {
		const handler = () => {
			const scrollTop = scrollContainerRef.current?.scrollTop || 0;
			setCollapsedColorPalette(scrollTop > 50);
		};
		return debounce(handler, 100);
	}, [setCollapsedColorPalette]);

	useEffect(
		() => () => {
			handleScroll.cancel();
		},
		[handleScroll]
	);

	const handleConfirmColorSelected = () => {
		if (!colorSelectedDrawer) return;

		saveColorCodeURL(colorSelectedDrawer?.code);

		let changeBaseColor: ChangeBaseColorPayload | undefined;
		if (base !== colorSelectedDrawer?.base) {
			const baseColorAttribute = superSkuAttributes?.find(
				(attr) =>
					attr.identifier.includes(BASE_COLOR_IDENTIFIER_SUFFIX) && attr.usage === USAGE_DEFINING
			);
			const baseValue = baseColorAttribute?.values.find(
				(val) => val.identifier === colorSelectedDrawer?.base
			);
			if (baseColorAttribute && baseValue) {
				const color = colorSelectedDrawer.code !== NOT_APPLICABLE ? colorSelectedDrawer.code : null;
				changeBaseColor = {
					name: baseColorAttribute.identifier,
					value: baseValue.identifier,
					id: baseColorAttribute.id,
					color: color ?? '',
				};
			}
		}
		colorDrawerActions.setColorDrawerData({
			isOpen: false,
			colorCodeSelected: colorSelectedDrawer,
			changeBaseColor,
			colorCode: colorSelectedDrawer?.code,
			range: pendingRange,
			hasConfirmedColor: true,
		});

		if (changeBaseColor) {
			setColorCode(colorSelectedDrawer?.code);
			onAttributeChange(changeBaseColor?.name, changeBaseColor?.value ?? '');
		}
	};

	const handleCloseDrawer = useCallback(() => {
		const range = colorCodeSelected?.base === WHITE_BASE_COLOR_NAME ? PASTELES_FAMILY_COLOR : null;
		const selectedRangeColors = allLineColors?.filter((lineColor) => lineColor.range === range)[0];

		// Reset transient state on close, but keep confirmed state if any. Without a
		// confirmed color, `colorCodeSelected` is just the base's default swatch, so
		// restoring it here would leave the CTA enabled with nothing actually selected.
		colorDrawerActions.setColorDrawerData({
			isOpen: false,
			changeBaseColor: undefined,
			colorSelectedDrawer: hasConfirmedColor ? colorCodeSelected : undefined,
			pendingRange: range,
			rangeColors: selectedRangeColors?.colors,
		});
		handleGoPalettes();
	}, [allLineColors, colorCodeSelected, colorDrawerActions, handleGoPalettes, hasConfirmedColor]);

	return (
		<SwipeableDrawer
			open={isOpen}
			onOpen={handleGoPalettes}
			onClose={handleCloseDrawer}
			anchor="right"
			sx={drawerSx}
			disableDiscovery
			disableSwipeToOpen
		>
			<Box sx={containerSx}>
				<DrawerTitle
					title={BuyByColor.labels.buyByColorCardTitle.t()}
					handleCloseDrawer={handleCloseDrawer}
					closeButtonAlign="left"
				/>
				<Box sx={buyByColorDrawerContainerSx}>
					{collapsedColorPalette ? (
						<Box onClick={handleGoPalettes} sx={buyByColorDrawerColorPaletteTitleSx}>
							<Typography
								variant="body1"
								fontWeight={OpenSansFontWeights.semibold}
								color="grey.500"
							>
								{BuyByColor.labels.familyColors.t()}
							</Typography>
							<Typography color="secondary.main">{colorDrawerState?.pendingRange}</Typography>
						</Box>
					) : (
						<Collapse in={!collapsedColorPalette}>
							<ColorsPaletteDefault
								isDrawer
								rangeLinkHandler={handleGoPalettes}
								colorRangeSelected={pendingRange ?? ''}
								allLineColors={allLineColors}
							/>
						</Collapse>
					)}
				</Box>
				<Box
					sx={overflowContentSx({ colorCodeSelected })}
					ref={scrollContainerRef}
					onScroll={handleScroll}
				>
					<Typography sx={rangeColorsTypographySx} variant="body1" color="grey.200">
						{rangeColors?.length ?? 0} {BuyByColor.labels.colors.t()}
					</Typography>
					<Grid container spacing={0} sx={rangeColorsGridSx} columns={4}>
						{rangeColors?.map((color, index) => {
							const isSelected = color.code === colorSelectedDrawer?.code;
							const isEndOfRow = (index + 1) % 4 === 0;
							return (
								<Grid item key={color.code} sx={rangeColorsGridItemSx({ isEndOfRow })}>
									<Box
										onClick={() =>
											colorDrawerActions.setColorDrawerData({
												colorSelectedDrawer: color,
											})
										}
										sx={colorSwatchBoxSx({ isMobile, isSelected })}
									>
										<Box sx={colorSwatchUpperSectionSx({ color })} />
										<Box sx={colorSwatchLowerSectionSx({ color })}>
											<Typography sx={colorSwatchTypography} variant="bodyCaption" color="grey.500">
												{capitalizeFirstLetter(color.name)}
											</Typography>
										</Box>
									</Box>
									{isSelected ? (
										<CheckCircleIcon sx={checkCircleIconSx({ isMobile, isEndOfRow })} />
									) : null}
								</Grid>
							);
						})}
					</Grid>
				</Box>
				<Box sx={colorCodeSelectedDrawerSx}>
					{colorCodeSelected ? (
						<Box sx={colorCodeBorderSx}>
							<ColorSelected isDrawer allLineColors={allLineColors} />
							<Button
								id="selectColorButton"
								variant="outlined"
								fullWidth
								onClick={handleConfirmColorSelected}
								disabled={!colorSelectedDrawer}
								sx={drawerSubmitButtonSx}
							>
								{BuyByColor.Buttons.selectColor.t()}
							</Button>
						</Box>
					) : null}
				</Box>
			</Box>
		</SwipeableDrawer>
	);
};
